import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { NotificationAction, NotificationService } from '../../../core/services/notification.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, TranslocoDirective],
  template: `
    @if (notification(); as notif) {
      <div
        *transloco="let t"
        [@fadeInOut]
        class="fixed inset-x-4 top-toast z-60 lg:inset-x-auto lg:right-4 lg:max-w-toast"
      >
        <div
          class="toast-app flex items-center compact-gap px-3 py-2 break-words"
          [ngClass]="edgeClasses[notif.type]"
          [attr.role]="role()"
          [attr.aria-live]="liveRegion()"
          (mouseenter)="hovered.set(true)"
          (mouseleave)="hovered.set(false)"
          (focusin)="focused.set(true)"
          (focusout)="focused.set(false)"
        >
          <span>{{ notif.message }}</span>
          @if (notif.action; as action) {
            <button
              class="btn btn-ghost px-2 text-inherit font-semibold"
              (click)="runAction(action)"
              [attr.aria-label]="action.label"
            >
              {{ action.label }}
            </button>
          }
          <button
            class="btn btn-ghost px-2 text-inherit"
            (click)="close()"
            [attr.aria-label]="t('common.close')"
          >
            ✕
          </button>
        </div>
      </div>
    }
  `,
  // Angular animation metadata cannot read CSS custom properties; 8px / 200ms mirror design.md § 6 (--duration-base, 4-8px slide).
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(8px)' })),
      ]),
    ]),
  ],
})
export class NotificationComponent {
  private readonly _notificationService = inject(NotificationService);
  public notification = toSignal(this._notificationService.notification$, {
    initialValue: null,
  });

  // Tracked apart: leaving the toast with the pointer must not resume the timer while a button in it still has focus.
  protected readonly hovered = signal(false);
  protected readonly focused = signal(false);
  protected readonly role = computed(() => (this.notification()?.type === 'error' ? 'alert' : 'status'));
  // An alert is announced assertively by its role alone.
  protected readonly liveRegion = computed(() => (this.role() === 'alert' ? null : 'polite'));

  constructor() {
    effect(() => this._notificationService.setPaused(this.hovered() || this.focused()));
    // A removed toast fires no mouseleave or focusout, so a stale flag would keep the next toast paused.
    effect(() => {
      if (this.notification()) return;
      untracked(() => {
        this.hovered.set(false);
        this.focused.set(false);
      });
    });
  }

  public edgeClasses: Record<string, string> = {
    error: 'toast-app--error',
    success: 'toast-app--success',
    warning: 'toast-app--warning',
    info: 'toast-app--info',
  };

  close() {
    this._notificationService.hide();
  }

  runAction(action: NotificationAction) {
    action.run();
    this._notificationService.hide();
  }
}
